import React, { useState, useEffect } from 'react';
import SwipeableRow from './SwipeableRow';
import ConfirmModal from './ConfirmModal';
import BoissonFormModal from './BoissonFormModal';

export default function AdminPanel({ onBack }) {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [data, setData] = useState(null);
  const [boissons, setBoissons] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('participants'); // participants | boissons
  const [toDelete, setToDelete] = useState(null); // { id, nom }
  const [boissonToDelete, setBoissonToDelete] = useState(null); // { id, nom }
  const [editingBoisson, setEditingBoisson] = useState(null); // boisson object | 'new' | null
  const [boissonError, setBoissonError] = useState('');

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      if (!res.ok) { setError('Mot de passe incorrect'); return; }
      setAuthenticated(true);
      loadData();
    } catch {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const res = await fetch('/api/admin/participations', {
        headers: { 'x-admin-password': password }
      });
      const d = await res.json();
      setData(d);
      const bRes = await fetch('/api/boissons');
      setBoissons(await bRes.json());
    } catch {
      setError('Erreur de chargement');
    }
  };

  const saveBoisson = async (payload) => {
    const isNew = editingBoisson === 'new';
    const url = isNew ? '/api/admin/boisson' : `/api/admin/boisson/${editingBoisson.id}`;
    const res = await fetch(url, {
      method: isNew ? 'POST' : 'PUT',
      headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
      body: JSON.stringify(payload)
    });
    const d = await res.json();
    if (!res.ok) throw new Error(d.error);
    setEditingBoisson(null);
    loadData();
  };

  const deleteBoisson = async () => {
    if (!boissonToDelete) return;
    try {
      const res = await fetch(`/api/admin/boisson/${boissonToDelete.id}`, {
        method: 'DELETE',
        headers: { 'x-admin-password': password }
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setBoissonToDelete(null);
      loadData();
    } catch (err) {
      setBoissonError(err.message || 'Impossible de supprimer cette boisson');
      setBoissonToDelete(null);
      setTimeout(() => setBoissonError(''), 4000);
    }
  };

  const deleteParticipant = async () => {
    if (!toDelete) return;
    try {
      await fetch(`/api/admin/participant/${toDelete.id}`, {
        method: 'DELETE',
        headers: { 'x-admin-password': password }
      });
      setToDelete(null);
      loadData();
    } catch {
      setError('Impossible de supprimer cet invité');
      setToDelete(null);
    }
  };

  // Grouper byBoisson
  const byBoisson = data?.byBoisson?.reduce((acc, row) => {
    if (!acc[row.boisson]) acc[row.boisson] = { emoji: row.emoji, quantite: row.quantite_souhaitee, participants: [], prisTotal: 0 };
    acc[row.boisson].participants.push(row.quantite > 1 ? `${row.participant} (x${row.quantite})` : row.participant);
    acc[row.boisson].prisTotal += row.quantite;
    return acc;
  }, {}) || {};

  if (!authenticated) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div className="container px-3" style={{ maxWidth: 380 }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#607d8b', fontWeight: 700, padding: '0 0 1.5rem', cursor: 'pointer' }}>
            <i className="bi bi-arrow-left me-1" />Retour
          </button>
          <div className="party-card p-4">
            <div className="text-center mb-4">
              <span style={{ fontSize: '2.5rem' }}>🔐</span>
              <h2 style={{ fontWeight: 800, color: '#1a2e35', fontSize: '1.3rem', margin: '0.5rem 0 0.3rem' }}>Administration</h2>
              <p style={{ color: '#607d8b', fontSize: '0.88rem', margin: 0 }}>Réservé à Gladys & Henry</p>
            </div>
            <form onSubmit={login}>
              <input
                type="password"
                className="name-input mb-3"
                placeholder="Mot de passe..."
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoFocus
              />
              {error && <div className="alert alert-danger py-2 px-3 rounded-pill text-center mb-3" style={{ fontSize: '0.85rem' }}>{error}</div>}
              <button type="submit" className="btn btn-party w-100" disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm" /> : 'Accéder'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '2rem' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #00acc1, #007c91)', padding: '1rem', color: '#fff' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', fontWeight: 700, padding: '0 0 0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
          <i className="bi bi-arrow-left me-1" />Retour
        </button>
        <div style={{ fontWeight: 800, fontSize: '1.2rem' }}>Administration 🔐</div>
        <div style={{ fontSize: '0.82rem', opacity: 0.85 }}>
          {data?.byParticipant?.length || 0} participant{(data?.byParticipant?.length || 0) > 1 ? 's' : ''} inscrits
        </div>
      </div>

      <div className="container px-3" style={{ maxWidth: 480, paddingTop: '1rem' }}>
        {/* Toggle vue */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <button
            onClick={() => setView('participants')}
            className={view === 'participants' ? 'btn btn-party' : 'btn btn-outline-secondary'}
            style={{ borderRadius: '50px', flex: 1, fontWeight: 700, fontSize: '0.88rem', padding: '0.5rem' }}
          >
            <i className="bi bi-people me-1" />Participants
          </button>
          <button
            onClick={() => setView('boissons')}
            className={view === 'boissons' ? 'btn btn-party' : 'btn btn-outline-secondary'}
            style={{ borderRadius: '50px', flex: 1, fontWeight: 700, fontSize: '0.88rem', padding: '0.5rem' }}
          >
            <i className="bi bi-cup-straw me-1" />Boissons
          </button>
        </div>

        {/* Vue participants */}
        {view === 'participants' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {(data?.byParticipant?.length || 0) > 0 && (
              <div style={{ textAlign: 'center', color: '#90a4ae', fontSize: '0.78rem', marginBottom: '0.2rem' }}>
                <i className="bi bi-arrow-left-short" />Glisse un invité vers la gauche pour le retirer
              </div>
            )}
            {(data?.byParticipant || []).map((p) => (
              <SwipeableRow key={p.id} onDelete={() => setToDelete({ id: p.id, nom: p.nom })}>
                <div className="party-card p-3">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#e0f7fa,#b2ebf2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#00acc1', fontSize: '1rem', flexShrink: 0 }}>
                      {p.nom.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 800, color: '#1a2e35' }}>{p.nom}</div>
                      <div style={{ fontSize: '0.82rem', color: '#607d8b' }}>
                        {p.boissons || <span style={{ color: '#ffb300' }}>Aucune boisson choisie</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </SwipeableRow>
            ))}
            {(data?.byParticipant?.length || 0) === 0 && (
              <div className="text-center py-4" style={{ color: '#90a4ae' }}>Aucun participant pour l'instant</div>
            )}
          </div>
        )}

        {/* Vue boissons */}
        {view === 'boissons' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            <button
              onClick={() => setEditingBoisson('new')}
              className="btn btn-party w-100 mb-1"
              style={{ fontSize: '0.9rem', padding: '0.6rem' }}
            >
              <i className="bi bi-plus-lg me-1" />Ajouter une boisson
            </button>
            {boissonError && (
              <div className="alert alert-danger py-2 px-3 rounded-pill text-center" style={{ fontSize: '0.85rem' }}>{boissonError}</div>
            )}
            {boissons.map((b) => {
              const info = byBoisson[b.nom];
              const prisTotal = Number(b.nombre_participants) || 0;
              return (
                <div key={b.id} className="party-card p-3">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: info?.participants?.length ? '0.5rem' : 0 }}>
                    <span style={{ fontSize: '1.3rem' }}>{b.emoji}</span>
                    <span style={{ fontWeight: 800, color: '#1a2e35', flex: 1 }}>{b.nom}</span>
                    <span style={{ fontWeight: 700, fontSize: '0.88rem', color: prisTotal >= b.quantite_souhaitee ? 'var(--party-success)' : 'var(--party-primary)' }}>
                      {prisTotal}/{b.quantite_souhaitee}
                    </span>
                    <button
                      onClick={() => setEditingBoisson(b)}
                      style={{ background: 'none', border: 'none', color: '#607d8b', padding: '0.2rem 0.3rem', cursor: 'pointer' }}
                    >
                      <i className="bi bi-pencil-fill" />
                    </button>
                    <button
                      onClick={() => setBoissonToDelete({ id: b.id, nom: b.nom })}
                      style={{ background: 'none', border: 'none', color: '#dc3545', padding: '0.2rem 0.3rem', cursor: 'pointer' }}
                    >
                      <i className="bi bi-trash3-fill" />
                    </button>
                  </div>
                  {info?.participants?.length > 0 && (
                    <div style={{ paddingLeft: '2.2rem' }}>
                      {info.participants.map((name, i) => (
                        <span key={i} style={{ display: 'inline-block', background: '#e0f7fa', color: '#006064', borderRadius: '20px', padding: '2px 10px', fontSize: '0.8rem', fontWeight: 700, marginRight: '0.3rem', marginBottom: '0.3rem' }}>
                          {name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="text-center mt-3">
          <button onClick={loadData} style={{ background: 'none', border: 'none', color: 'var(--party-primary)', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
            <i className="bi bi-arrow-clockwise me-1" />Actualiser
          </button>
        </div>
      </div>

      {toDelete && (
        <ConfirmModal
          title={`Retirer ${toDelete.nom} ?`}
          message="Les boissons que cet invité avait choisi d'apporter seront libérées pour les autres."
          confirmLabel="Retirer"
          onConfirm={deleteParticipant}
          onCancel={() => setToDelete(null)}
        />
      )}

      {boissonToDelete && (
        <ConfirmModal
          title={`Supprimer ${boissonToDelete.nom} ?`}
          message="Cette boisson sera retirée de la liste proposée aux invités."
          confirmLabel="Supprimer"
          onConfirm={deleteBoisson}
          onCancel={() => setBoissonToDelete(null)}
        />
      )}

      {editingBoisson && (
        <BoissonFormModal
          boisson={editingBoisson === 'new' ? null : editingBoisson}
          onSave={saveBoisson}
          onCancel={() => setEditingBoisson(null)}
        />
      )}
    </div>
  );
}
